import { put, takeLatest, all, fork, join, select } from 'redux-saga/effects';

import i18n from '../../../../i18n/i18n';
import { copyToClipboard } from '@utils/clipboard';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import {
  putStaffPageLoadingStatus,
  setPhoneUpdateLoading,
  finishPhoneUpdate,
  showStaffPageError,
  STAFF_PAGE_ACTIONS,
  putStaffPage,
  putStaffPageBusinessUnits,
  showToast,
  fetchStaffPage,
} from './actions';
import {
  sendStaffUserStatus,
  sendStaffUserBusinessUnits,
  getStaffUserInformation,
  adminResetPassword,
  sendMentorEnableTimeTracking,
  sendUserExperimentalFeatures,
} from './services';
import { adminUpdateUserMfa, adminUpdateUserName, adminUpdateUserPhone } from '@services/adminUserAuthService';
import { AUTH_BLOCKED_REASONS, TOAST_TYPES } from './consts';
import { getAllBusinessUnits } from '@containers/Admin/shared/businessUnitService';

export function* callFetchOneStaffUser(action) {
  try {
    const id = action.payload;
    yield put(putStaffPageLoadingStatus(true));
    const currentUserRoles = yield select((state) => state.getIn(['app', 'authentication', 'user', 'roles']).toJS());
    const isTechAdmin = Array.isArray(currentUserRoles) && currentUserRoles.includes('40');

    const res = yield fork(getStaffUserInformation, id);
    const businessUnitsData = isTechAdmin ? yield fork(getAllBusinessUnits) : null;

    const { userData, errors } = yield join(res);
    const allBusinessUnitsResult = businessUnitsData ? yield join(businessUnitsData) : { allBusinessUnits: [] };
    const { allBusinessUnits, errors: businessUnitsErrors } = allBusinessUnitsResult;

    if (errors || businessUnitsErrors) {
      yield put(showStaffPageError());
    } else {
      yield put(putStaffPageBusinessUnits(allBusinessUnits));
      yield put(putStaffPage(userData));
    }
  } catch {
    yield put(showStaffPageError());
  } finally {
    yield put(putStaffPageLoadingStatus(false));
  }
}

export function* callChangeStaffUserStatus({ payload: { userId, status } }) {
  try {
    const { userData } = yield select((state) => state.get('staffPage').toJS());
    const res = yield fork(sendStaffUserStatus, userId, status);
    const { errors } = yield join(res);

    if (errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
    } else {
      yield put(fetchStaffPage(userData.id));
      yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_ATTACHED));
    }
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  }
}

export function* callChangeStaffBusinessUnits({ payload: { businessUnits, userId } }) {
  try {
    const { userData } = yield select((state) => state.get('staffPage').toJS());
    const res = yield fork(sendStaffUserBusinessUnits, userId, businessUnits);
    const { errors } = yield join(res);

    if (errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
    } else {
      yield put(fetchStaffPage(userData.id));
      yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_ATTACHED));
    }
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  }
}

export function* callChangeMentorEnableTimeTracking({ payload: { userId, enableTimeTracking } }) {
  try {
    const { userData } = yield select((state) => state.get('staffPage').toJS());
    const res = yield fork(sendMentorEnableTimeTracking, userId, enableTimeTracking);
    const { errors } = yield join(res);

    if (errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
    } else {
      yield put(fetchStaffPage(userData.id));
      yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_UPDATED));
    }
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  }
}

export function* callChangeUserExperimentalFeatures({ payload: { userId, experimentalFeatures } }) {
  try {
    const { userData } = yield select((state) => state.get('staffPage').toJS());
    const res = yield fork(sendUserExperimentalFeatures, userId, experimentalFeatures);
    const { errors } = yield join(res);

    if (errors) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
    } else {
      yield put(fetchStaffPage(userData.id));
      yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_UPDATED));
    }
  } catch {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  }
}

export function* callResetStaffUserPassword({ payload: { userId } }) {
  try {
    yield put(putStaffPageLoadingStatus(true));

    const { userData } = yield select((state) => state.get('staffPage').toJS());
    const userEmail = userData?.email || userData?.userName || '';

    const result = yield adminResetPassword(userId);
    const blockedReason = result?.blockedReason;
    const temporaryPassword = result?.temporaryPassword;
    const emailSent = result?.emailSent;

    // Backward-compatible guard:
    // Treat nulls as "inactive blocked" even if blockedReason is not present.
    const isBlockedInactive =
      blockedReason === AUTH_BLOCKED_REASONS.USER_INACTIVE ||
      (temporaryPassword === null && emailSent === null);
    if (isBlockedInactive) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ACTIVATE_USER_FIRST));
      return;
    }

    if (!temporaryPassword) throw new Error('Missing temporary password');

    const deliveryBody =
      emailSent === false
        ? i18n.t('Authentication.Password.adminTempPasswordEmailFailed', {
            email: userEmail,
          })
        : i18n.t('Authentication.Password.adminTempPasswordEmailSent', {
            email: userEmail,
          });

    const handleCopy = () => {
      copyToClipboard(temporaryPassword);
    };

    yield put(
      openPopUp(POPUP_TYPES.CONFIRM, {
        titleText: i18n.t('Authentication.Password.adminTempPasswordTitle'),
        bodyText: (
          <>
            {i18n.t('Authentication.Password.adminTempPasswordBody')}
            <br />
            <span style={{ fontWeight: 900, fontSize: '22px', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
              {temporaryPassword}
            </span>
            <br />
            {deliveryBody}
          </>
        ),
        confirmText: i18n.t('Authentication.Password.adminTempPasswordCopyButton'),
        cancelText: i18n.t('Actions.Instance.close'),
        onConfirm: (confirm) => confirm && handleCopy(),
        disableBackdrop: true,
      }),
    );

    yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_PASSWORD_RESET));
  } catch (e) {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_PASSWORD_RESET));
  } finally {
    yield put(putStaffPageLoadingStatus(false));
  }
}

// Saga to handle admin MFA update
export function* callUpdateStaffUserMfa({ payload: { userId, method } }) {
  try {
    yield put(putStaffPageLoadingStatus(true));

    const result = yield adminUpdateUserMfa(userId, method);
    const blockedReason = result?.blockedReason;
    if (blockedReason === AUTH_BLOCKED_REASONS.USER_INACTIVE) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_ACTIVATE_USER_FIRST));
      return;
    }
    if (blockedReason === AUTH_BLOCKED_REASONS.USER_NOT_IN_COGNITO) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_MFA_USER_NOT_IN_COGNITO));
      return;
    }
    if (blockedReason === AUTH_BLOCKED_REASONS.PHONE_NUMBER_REQUIRED) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_MFA_PHONE_REQUIRED));
      return;
    }

    if (!result?.success) {
      throw new Error('Failed to update MFA');
    }

    yield put(fetchStaffPage(userId));
    yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_MFA_UPDATED));
  } catch (e) {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_MFA_UPDATED));
  } finally {
    yield put(putStaffPageLoadingStatus(false));
  }
}

export function* callUpdateStaffUserPhone({ payload: { userId, phoneNumber } }) {
  let updatedPhone;
  try {
    yield put(setPhoneUpdateLoading(true));
    const result = yield adminUpdateUserPhone(userId, phoneNumber);

    if (result?.blockedReason === AUTH_BLOCKED_REASONS.SMS_MFA_ENABLED) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_REMOVE_PHONE_SMS_MFA));
      return;
    }
    if (result?.blockedReason === AUTH_BLOCKED_REASONS.USER_NOT_IN_COGNITO) {
      yield put(showToast(TOAST_TYPES.SHOW_ERROR_PHONE_USER_NOT_IN_COGNITO));
      return;
    }
    if (!result?.success) {
      throw new Error('Failed to update phone number');
    }

    updatedPhone = result.phone;
    yield put(fetchStaffPage(userId));
    yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_PHONE_UPDATED));
  } catch (e) {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_PHONE_UPDATED));
  } finally {
    yield put(finishPhoneUpdate(updatedPhone));
  }
}

export function* callUpdateStaffUserName({ payload: { userId, firstName, lastName } }) {
  try {
    yield put(putStaffPageLoadingStatus(true));
    const result = yield adminUpdateUserName(userId, firstName, lastName);
    if (!result?.success) throw new Error('Failed to update staff user name');
    yield put(fetchStaffPage(userId));
    yield put(showToast(TOAST_TYPES.SHOW_SUCCESS_UPDATED));
  } catch (e) {
    yield put(showToast(TOAST_TYPES.SHOW_ERROR_ATTACHED));
  } finally {
    yield put(putStaffPageLoadingStatus(false));
  }
}

function* watchCallFetchStaffUser() {
  yield takeLatest(STAFF_PAGE_ACTIONS.FETCH_ONE_STAFF, callFetchOneStaffUser);
}

function* watchCallChangeStaffUserStatus() {
  yield takeLatest(STAFF_PAGE_ACTIONS.CHANGE_STAFF_STATUS, callChangeStaffUserStatus);
}

function* watchCallChangeStaffBusinessUnits() {
  yield takeLatest(STAFF_PAGE_ACTIONS.CHANGE_STAFF_BUSINESS_UNITS, callChangeStaffBusinessUnits);
}

function* watchCallChangeMentorEnableTimeTracking() {
  yield takeLatest(STAFF_PAGE_ACTIONS.CHANGE_MENTOR_ENABLE_TIME_TRACKING, callChangeMentorEnableTimeTracking);
}

function* watchCallChangeUserExperimentalFeatures() {
  yield takeLatest(STAFF_PAGE_ACTIONS.CHANGE_USER_EXPERIMENTAL_FEATURES, callChangeUserExperimentalFeatures);
}

function* watchCallResetStaffUserPassword() {
  yield takeLatest(STAFF_PAGE_ACTIONS.RESET_STAFF_PASSWORD, callResetStaffUserPassword);
}

function* watchCallUpdateStaffUserMfa() {
  yield takeLatest(STAFF_PAGE_ACTIONS.UPDATE_STAFF_MFA, callUpdateStaffUserMfa);
}

function* watchCallUpdateStaffUserPhone() {
  yield takeLatest(STAFF_PAGE_ACTIONS.UPDATE_STAFF_PHONE, callUpdateStaffUserPhone);
}

function* watchCallUpdateStaffUserName() {
  yield takeLatest(STAFF_PAGE_ACTIONS.UPDATE_STAFF_NAME, callUpdateStaffUserName);
}

export default function* staffPageSaga() {
  yield all([
    fork(watchCallFetchStaffUser),
    fork(watchCallChangeStaffUserStatus),
    fork(watchCallChangeStaffBusinessUnits),
    fork(watchCallChangeMentorEnableTimeTracking),
    fork(watchCallChangeUserExperimentalFeatures),
    fork(watchCallResetStaffUserPassword),
    fork(watchCallUpdateStaffUserMfa),
    fork(watchCallUpdateStaffUserPhone),
    fork(watchCallUpdateStaffUserName),
  ]);
}
